// src/components/Building.tsx

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { useCursor } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import type { BuildingForRender } from '../hooks/useCityData';
import type { LocalPoint } from '../utils/osmParser';

// ----------------------------------------------------------------------
// Props del componente
// ----------------------------------------------------------------------

export interface BuildingProps {
  /** Datos del edificio con anillos en coordenadas locales */
  building: BuildingForRender;
  /** Callback cuando se selecciona el edificio */
  onSelect?: (building: BuildingForRender, mesh: THREE.Mesh) => void;
  /** Si el edificio está seleccionado actualmente */
  selected?: boolean;
  /** Color opcional (si no se quiere usar el color por tipo) */
  color?: string;
  /** Altura opcional (sobrescribe la del building) */
  heightOverride?: number;
  /** Opacidad (por defecto 1) */
  opacity?: number;
  /** Si se debe mostrar contorno al seleccionar */
  showOutline?: boolean;
}

// ----------------------------------------------------------------------
// Constantes de colores por tipo de edificio
// ----------------------------------------------------------------------

const BUILDING_COLORS: Record<string, string> = {
  house: '#D4A574',
  detached: '#D4A574',
  residential: '#CCBBAA',
  apartments: '#B8A99A',
  commercial: '#8899AA',
  retail: '#8FA3B0',
  industrial: '#998877',
  office: '#667788',
  school: '#C4A882',
  hospital: '#B87B6B',
  church: '#A89880',
  default: '#CCCCCC',
};

function getColorByType(tags: Record<string, string>): string {
  const type = tags.building || 'default';
  return BUILDING_COLORS[type] || BUILDING_COLORS.default;
}

// ----------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------

const Building: React.FC<BuildingProps> = ({
  building,
  onSelect,
  selected = false,
  color,
  heightOverride,
  opacity = 1,
  showOutline = true,
}) => {
  const [hovered, setHovered] = useState(false);
  useCursor(hovered);

  // Crear geometría a partir de los anillos
  const geometry = useMemo(() => {
    const { outerRing, innerRings } = building;
    if (!outerRing || outerRing.length < 3) return null;

    // Usamos (x, -z) para anular el espejo de rotateX(-PI/2) y que los
    // edificios queden en las mismas posiciones que las calles.
    const createRing = (ring: LocalPoint[]) => {
      const shapeOrPath = new THREE.Path();
      shapeOrPath.moveTo(ring[0].x, -ring[0].z);
      for (let i = 1; i < ring.length; i++) {
        shapeOrPath.lineTo(ring[i].x, -ring[i].z);
      }
      shapeOrPath.closePath();
      return shapeOrPath;
    };

    // Crear forma principal (anillo exterior)
    const shape = new THREE.Shape(createRing(outerRing).getPoints());

    // Agregar huecos (anillos interiores)
    innerRings.forEach((ring) => {
      if (ring.length < 3) return;
      shape.holes.push(createRing(ring));
    });

    // Extruir
    const height = heightOverride ?? building.height ?? 10;
    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      depth: height,
      bevelEnabled: false,
    };
    const geom = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    // Rotar -90° en X para que la extrusión quede vertical
    geom.rotateX(-Math.PI / 2);

    // El espejo en Z + rotación invierte el winding de los triángulos;
    // invertimos el orden de los índices para restaurar las normales exteriores
    const index = geom.getIndex();
    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        const tmp = index.getX(i);
        index.setX(i, index.getX(i + 2));
        index.setX(i + 2, tmp);
      }
    }

    // Calcular normales correctas
    geom.computeVertexNormals();

    return geom;
  }, [building, heightOverride]);

  // Material
  const material = useMemo(() => {
    const baseColor =
      color || getColorByType(building.tags || {});
    return new THREE.MeshStandardMaterial({
      color: baseColor,
      roughness: 0.7,
      metalness: 0.1,
      transparent: opacity < 1,
      opacity,
    });
  }, [building.tags, color, opacity]);

  // Liberar geometría y material al desmontar o al regenerarse
  useEffect(() => {
    return () => {
      geometry?.dispose();
      material?.dispose();
    };
  }, [geometry, material]);

  // Manejador de click
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();
      if (onSelect) {
        onSelect(building, event.object as THREE.Mesh);
      }
    },
    [building, onSelect]
  );

  // Si no hay geometría válida, no renderizar
  if (!geometry) return null;

  return (
    <group>
      {/* Mesh principal del edificio */}
      <mesh
        geometry={geometry}
        material={material}
        position={[0, 0, 0]}
        onClick={handleClick}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          setHovered(false);
        }}
        castShadow
        receiveShadow
      />

      {/* Contorno de selección */}
      {selected && showOutline && (
        <lineSegments>
          <edgesGeometry args={[geometry]} />
          <lineBasicMaterial color="#ff9900" linewidth={2} />
        </lineSegments>
      )}

      {/* Indicador de hover (ligero resaltado) */}
      {hovered && !selected && (
        <mesh geometry={geometry} position={[0, 0.05, 0]}>
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={0.2}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
};

export default Building;